/**
 * Time/Date Utility Functions
 * JS equivalents of PHP Interpals\Helpers\Datetime methods
 * Uses i18n module for translations when available
 */

/**
 * Get plural form index for a count
 * Supports plural rules for various languages:
 * - English, Spanish, German, etc.: 2 forms (singular/plural)
 * - Russian: 3 forms
 * - Arabic: 6 forms
 * - Japanese, Korean: 1 form (no pluralization)
 * @param {number} count
 * @returns {number} Index of plural form to use
 */
function getPluralFormIndex(count) {
    const locale = window.I18n?.getLocale() || 'en_US';

    // Arabic: 6 forms
    // Form 0: n == 0
    // Form 1: n == 1
    // Form 2: n == 2
    // Form 3: n % 100 >= 3 && n % 100 <= 10 (few)
    // Form 4: n % 100 >= 11 (many)
    // Form 5: everything else (other)
    if (locale.startsWith('ar')) {
        if (count === 0) return 0;
        if (count === 1) return 1;
        if (count === 2) return 2;
        const mod100 = count % 100;
        if (mod100 >= 3 && mod100 <= 10) return 3;
        if (mod100 >= 11) return 4;
        return 5;
    }

    // Russian: 3 forms
    // Form 0: 1, 21, 31, 41... (ends in 1, except 11)
    // Form 1: 2-4, 22-24, 32-34... (ends in 2-4, except 12-14)
    // Form 2: 0, 5-20, 25-30... (everything else)
    if (locale.startsWith('ru')) {
        const mod10 = count % 10;
        const mod100 = count % 100;

        if (mod10 === 1 && mod100 !== 11) {
            return 0;
        }
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return 1;
        }
        return 2;
    }

    // Ukrainian: 3 forms, same selector as Russian.
    // Form 0: ends in 1, except 11. Form 1: ends in 2-4, except 12-14.
    // Form 2: everything else.
    if (locale.startsWith('uk')) {
        const mod10 = count % 10;
        const mod100 = count % 100;

        if (mod10 === 1 && mod100 !== 11) {
            return 0;
        }
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return 1;
        }
        return 2;
    }

    // Polish: 3 forms. Form 0 is 1 alone, so 21 and 101 take form 2, not form 0.
    // Matches the pl_PL Plural-Forms header the catalogs are written against.
    if (locale.startsWith('pl')) {
        if (count === 1) return 0;
        const mod10 = count % 10;
        const mod100 = count % 100;
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
            return 1;
        }
        return 2;
    }

    // Czech: 3 forms. Form 0: 1. Form 1: 2-4. Form 2: everything else.
    if (locale.startsWith('cs')) {
        if (count === 1) return 0;
        if (count >= 2 && count <= 4) return 1;
        return 2;
    }

    // Romanian: 3 forms. Form 0: 1. Form 1: 0, or n % 100 in 1-19.
    // Form 2: everything else, which takes the "de" genitive.
    // The 1-19 bound follows the ro_RO Plural-Forms header, which the catalog
    // values are written against. CLDR draws its "few" at 2-19 instead.
    if (locale.startsWith('ro')) {
        if (count === 1) return 0;
        const mod100 = count % 100;
        if (count === 0 || (mod100 >= 1 && mod100 <= 19)) return 1;
        return 2;
    }

    // Japanese, Korean: 1 form (no pluralization)
    if (locale.startsWith('ja') || locale.startsWith('ko')) {
        return 0;
    }

    // English and most other languages: 2 forms (singular/plural)
    // French uses "plural > 1" but for most cases this works the same
    return count === 1 ? 0 : 1;
}

/**
 * Translate a plural string with count parameter
 * @param {string} key - Translation key
 * @param {number} count - Count for plural selection
 * @returns {string} Translated string with count substituted
 */
export function tPlural(key, count) {
    const translation = tt(key, { count });

    // If translation contains pipe-separated forms, select the right one
    if (translation.includes('|')) {
        const forms = translation.split('|');
        const formIndex = getPluralFormIndex(count);
        const selectedForm = forms[Math.min(formIndex, forms.length - 1)];
        // Replace {count} placeholder with actual count
        return selectedForm.replace(/{count}/g, count);
    }

    return translation;
}

/**
 * Simple translation helper
 * @param {string} key - Translation key
 * @param {Object} params - Parameters to substitute
 * @returns {string} Translated string
 */
function tt(key, params = {}) {
    // Guarded: this module is imported ahead of translate-fallback.js and in tests without it.
    return typeof window.tOr === 'function' ? window.tOr(key, key, params) : key;
}

/**
 * Format relative time in concise format (5s, 2m, 3h, 2d, 3w, 1y)
 * Matches PHP Datetime::formatTimeRelative($date, true)
 * @param {string|Date} dateString
 * @param {(key: string, params?: Object) => string} [translate] - Resolves the common.* unit keys; an empty result falls back to English
 * @returns {string} An empty string for an unparseable time
 */
export function formatTimeRelativeShort(dateString, translate = tt) {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    // A time ahead of the local clock reads as now.
    const diffSecs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffSecs / 3600);
    const diffDays = Math.floor(diffSecs / 86400);
    const diffWeeks = Math.floor(diffSecs / 604800);
    const diffYears = Math.floor(diffSecs / 31536000);

    if (diffSecs === 0) {
        return translate('common.now') || 'Now';
    }
    if (diffSecs < 60) {
        return translate('common.time_seconds_short', { count: diffSecs }) || `${diffSecs}s`;
    }
    if (diffMins < 60) {
        return translate('common.time_minutes_short', { count: diffMins }) || `${diffMins}m`;
    }
    if (diffHours < 24) {
        return translate('common.time_hours_short', { count: diffHours }) || `${diffHours}h`;
    }
    if (diffDays < 7) {
        return translate('common.time_days_short', { count: diffDays }) || `${diffDays}d`;
    }
    if (diffWeeks < 52) {
        return translate('common.time_weeks_short', { count: diffWeeks }) || `${diffWeeks}w`;
    }
    return translate('common.time_years_short', { count: diffYears }) || `${diffYears}y`;
}

/**
 * Format a duration in seconds as a concise localized string (e.g., "5d 3h", "2h 30m")
 * Uses the same i18n keys as formatTimeRelativeShort but for arbitrary durations.
 * @param {number} seconds - Duration in seconds
 * @returns {string}
 */
export function formatDurationShort(seconds) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) {
        const d = tt('common.time_days_short', { count: days }) || `${days}d`;
        if (hours > 0) {
            const h = tt('common.time_hours_short', { count: hours }) || `${hours}h`;
            return `${d} ${h}`;
        }
        return d;
    }
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        const h = tt('common.time_hours_short', { count: hours }) || `${hours}h`;
        if (minutes > 0) {
            const m = tt('common.time_minutes_short', { count: minutes }) || `${minutes}m`;
            return `${h} ${m}`;
        }
        return h;
    }
    return tt('common.time_minutes_short', { count: Math.max(1, minutes) }) || `${Math.max(1, minutes)}m`;
}

/**
 * Format relative time in long format (5 minutes ago, 2 days ago)
 * Matches PHP Datetime::formatTimeRelative($date, false)
 * @param {string|Date} dateString
 * @returns {string}
 */
export function formatTimeRelativeLong(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) {
        return tt('common.just_now') || 'just now';
    }
    if (diffMins < 60) {
        return tPlural('common.time_minutes_ago', diffMins) || `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    }
    if (diffHours < 24) {
        return tPlural('common.time_hours_ago', diffHours) || `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    }
    if (diffDays < 30) {
        return tPlural('common.time_days_ago', diffDays) || `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    }

    return date.toLocaleDateString();
}

/**
 * Format relative time for future dates (in 3 days, in 2 weeks, etc.)
 * For past dates, returns "X days ago" or "passed"
 * @param {string|Date} dateString
 * @returns {string}
 */
export function formatFutureRelative(dateString) {
    // A bare YYYY-MM-DD parses as UTC midnight, which is the previous day west of UTC
    const date = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)
        ? new Date(dateString + 'T00:00:00')
        : new Date(dateString);
    const now = new Date();
    // Reset time to compare just dates
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const diffMs = date - now;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
        const absDays = Math.abs(diffDays);
        if (absDays === 1) return tt('common.yesterday') || 'yesterday';
        if (absDays < 7) return tPlural('common.time_days_ago', absDays) || `${absDays} days ago`;
        if (absDays < 30) {
            const weeks = Math.floor(absDays / 7);
            return tPlural('common.time_weeks_ago', weeks) || `${weeks} weeks ago`;
        }
        if (absDays < 365) {
            const months = Math.floor(absDays / 30);
            return tPlural('common.time_months_ago', months) || `${months} ${months === 1 ? 'month' : 'months'} ago`;
        }
        const years = Math.floor(absDays / 365);
        return tPlural('common.time_years_ago', years) || `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }
    if (diffDays === 0) return tt('common.today') || 'today';
    if (diffDays === 1) return tt('common.tomorrow') || 'tomorrow';
    if (diffDays < 7) return tPlural('common.time_in_days', diffDays) || `in ${diffDays} days`;
    if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return tPlural('common.time_in_weeks', weeks) || `in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    }
    if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return tPlural('common.time_in_months', months) || `in ${months} ${months === 1 ? 'month' : 'months'}`;
    }
    const years = Math.floor(diffDays / 365);
    return tPlural('common.time_in_years', years) || `in ${years} ${years === 1 ? 'year' : 'years'}`;
}

/**
 * Get localized short month name
 * @param {number} monthIndex - 0-based month index (0 = January)
 * @returns {string} Localized short month name
 */
export function getLocalizedMonthShort(monthIndex) {
    const monthKeys = [
        'common.month_jan_short',
        'common.month_feb_short',
        'common.month_mar_short',
        'common.month_apr_short',
        'common.month_may_short',
        'common.month_jun_short',
        'common.month_jul_short',
        'common.month_aug_short',
        'common.month_sep_short',
        'common.month_oct_short',
        'common.month_nov_short',
        'common.month_dec_short'
    ];
    const fallback = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const translated = tt(monthKeys[monthIndex]);
    // If translation returns the key itself, use fallback
    if (translated === monthKeys[monthIndex]) {
        return fallback[monthIndex];
    }
    return translated;
}

/**
 * Get localized full month name
 * @param {number} monthIndex - 0-based month index (0 = January)
 * @returns {string} Localized full month name
 */
export function getLocalizedMonthFull(monthIndex) {
    const monthKeys = [
        'common.month_january_date',
        'common.month_february_date',
        'common.month_march_date',
        'common.month_april_date',
        'common.month_may_date',
        'common.month_june_date',
        'common.month_july_date',
        'common.month_august_date',
        'common.month_september_date',
        'common.month_october_date',
        'common.month_november_date',
        'common.month_december_date'
    ];
    const fallback = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

    const translated = tt(monthKeys[monthIndex]);
    // If translation returns the key itself, use fallback
    if (translated === monthKeys[monthIndex]) {
        return fallback[monthIndex];
    }
    return translated;
}

/**
 * Format a date range with localized month names
 * @param {string} startDate - Start date string (YYYY-MM-DD)
 * @param {string} endDate - End date string (YYYY-MM-DD)
 * @returns {string} Formatted date range (e.g., "Jan 21-24" or "Jan 21 - Feb 5")
 */
export function formatDateRangeShort(startDate, endDate) {
    if (!startDate) return '';

    const start = new Date(startDate + 'T00:00:00');
    const end = endDate ? new Date(endDate + 'T00:00:00') : start;

    const startMonth = getLocalizedMonthShort(start.getMonth());
    const startDay = start.getDate();
    const endMonth = getLocalizedMonthShort(end.getMonth());
    const endDay = end.getDate();

    // Same month
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        if (startDay === endDay) {
            return `${startMonth} ${startDay}`;
        }
        return `${startMonth} ${startDay}-${endDay}`;
    }

    // Different months
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
}

// Expose helpers globally for use in Alpine.js and inline scripts
if (typeof window !== 'undefined') {
    window.tPlural = tPlural;
    window.formatTimeRelativeShort = formatTimeRelativeShort;
    window.formatDurationShort = formatDurationShort;
    window.formatTimeRelativeLong = formatTimeRelativeLong;
    window.formatFutureRelative = formatFutureRelative;
    window.getLocalizedMonthShort = getLocalizedMonthShort;
    window.getLocalizedMonthFull = getLocalizedMonthFull;
    window.formatDateRangeShort = formatDateRangeShort;
}
